import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FileText, ExternalLink, ArrowRight, CheckCircle, AlertCircle,
  DollarSign, Calculator, Clock, HelpCircle, Building2, Users
} from 'lucide-react';

const FinancialAid = () => {
  const financialAidForms = [
    {
      name: "FAFSA (Free Application for Federal Student Aid)",
      description: "The primary form for U.S. federal student aid, required by all U.S. colleges.",
      link: "https://studentaid.gov/h/apply-for-aid/fafsa",
      icon: "🏛️",
      deadline: "Varies by state (check your state deadline)",
      opensDate: "October 1st annually",
      cost: "Free",
      forWhom: "U.S. citizens, permanent residents, and eligible non-citizens",
      requirements: [
        "Social Security Number",
        "Federal tax returns (IRS Data Retrieval Tool recommended)",
        "W-2 forms and records of income",
        "Bank statements and investment records",
        "FSA ID for student and parent"
      ],
      tips: [
        "File as early as possible after October 1st",
        "Use IRS Data Retrieval Tool for accurate tax data",
        "List colleges in priority order (some states award by listing order)",
        "Review and correct your Student Aid Report (SAR)"
      ]
    },
    {
      name: "CSS Profile",
      description: "Additional financial aid form required by ~400 colleges for institutional aid.",
      link: "https://cssprofile.collegeboard.org/",
      icon: "📊",
      deadline: "Varies by college (check each college's deadline)",
      opensDate: "October 1st annually",
      cost: "$25 first school, $16 each additional (fee waivers available)",
      forWhom: "Students applying to colleges that require CSS Profile",
      requirements: [
        "Everything needed for FAFSA plus:",
        "Parent asset information in more detail",
        "Business/farm supplements if applicable",
        "Non-custodial parent information (some colleges)",
        "College Board account"
      ],
      tips: [
        "Check which colleges require it before applying",
        "Some colleges require non-custodial parent form",
        "Apply for fee waivers if eligible",
        "Answer all questions - incomplete forms delay aid"
      ]
    },
    {
      name: "IDOC (Institutional Documentation Service)",
      description: "Document submission service for colleges using CSS Profile.",
      link: "https://pages.collegeboard.org/idoc",
      icon: "📁",
      deadline: "As requested by colleges",
      opensDate: "After CSS Profile submission",
      cost: "Free",
      forWhom: "Students whose colleges request documentation through IDOC",
      requirements: [
        "Tax returns (student and parent)",
        "W-2 forms",
        "Business/farm documents if applicable",
        "Any additional forms requested by specific colleges"
      ],
      tips: [
        "Submit documents promptly after receiving IDOC request",
        "Keep copies of everything you submit",
        "Check status regularly in your IDOC account",
        "Contact colleges directly for institution-specific forms"
      ]
    }
  ];

  const internationalStudentInfo = {
    title: "International Students",
    description: "International students typically cannot file FAFSA. Here are your options:",
    options: [
      {
        name: "CSS Profile",
        detail: "Some colleges use CSS Profile to award institutional aid to international students"
      },
      {
        name: "College-Specific Forms",
        detail: "Many colleges have their own financial aid forms for international students"
      },
      {
        name: "External Scholarships",
        detail: "Apply for scholarships specifically for international students"
      },
      {
        name: "Home Country Aid",
        detail: "Check if your government offers study abroad scholarships"
      }
    ],
    link: "/paying-for-college"
  };

  const timeline = [
    { month: "October 1", event: "FAFSA and CSS Profile open for upcoming academic year" },
    { month: "October - November", event: "Submit FAFSA and CSS Profile (early action deadlines)" },
    { month: "January - February", event: "Regular decision financial aid deadlines" },
    { month: "March - April", event: "Financial aid award letters arrive" },
    { month: "May 1", event: "National Decision Day - commit to a college" },
    { month: "June - August", event: "Complete verification if selected, accept final aid package" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white py-20">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <DollarSign className="h-16 w-16 mx-auto mb-6 opacity-90" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Financial Aid Forms</h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            Complete guide to FAFSA, CSS Profile, and financial aid applications
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-xl text-gray-700 leading-relaxed">
              Financial aid can significantly reduce your college costs. Understanding which forms 
              to complete and when to submit them is crucial for maximizing your aid opportunities.
            </p>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="text-center p-4">
              <Calculator className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-emerald-600">85%</div>
              <div className="text-sm text-gray-600">Students receive aid</div>
            </Card>
            <Card className="text-center p-4">
              <DollarSign className="h-8 w-8 text-teal-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-teal-600">$14K</div>
              <div className="text-sm text-gray-600">Average grant amount</div>
            </Card>
            <Card className="text-center p-4">
              <Clock className="h-8 w-8 text-cyan-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-cyan-600">30 min</div>
              <div className="text-sm text-gray-600">FAFSA completion time</div>
            </Card>
            <Card className="text-center p-4">
              <Building2 className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-blue-600">~400</div>
              <div className="text-sm text-gray-600">Colleges use CSS Profile</div>
            </Card>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-8">Financial Aid Timeline</h2>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-emerald-200" />
            <div className="space-y-6">
              {timeline.map((item, index) => (
                <div key={index} className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className="flex-1 hidden md:block" />
                  <div className="absolute left-4 md:left-1/2 w-4 h-4 bg-emerald-500 rounded-full -translate-x-1/2 border-4 border-white" />
                  <div className="flex-1 ml-12 md:ml-0 md:px-8">
                    <Card className={`p-4 ${index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'}`}>
                      <div className="font-semibold text-emerald-600">{item.month}</div>
                      <div className="text-gray-700">{item.event}</div>
                    </Card>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Financial Aid Forms */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Required Forms</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Detailed information about each financial aid form, requirements, and tips.
            </p>
          </div>

          <div className="space-y-8">
            {financialAidForms.map((form, index) => (
              <Card key={index} className="overflow-hidden" data-testid={`form-card-${index}`}>
                <CardHeader className="bg-gradient-to-r from-gray-50 to-white">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <span className="text-4xl">{form.icon}</span>
                      <div>
                        <CardTitle className="text-xl">{form.name}</CardTitle>
                        <p className="text-gray-600 text-sm mt-1">{form.description}</p>
                        <div className="flex flex-wrap gap-4 mt-3 text-sm">
                          <span className="flex items-center gap-1 text-emerald-600">
                            <DollarSign className="h-4 w-4" /> {form.cost}
                          </span>
                          <span className="flex items-center gap-1 text-blue-600">
                            <Clock className="h-4 w-4" /> Opens: {form.opensDate}
                          </span>
                        </div>
                      </div>
                    </div>
                    <a href={form.link} target="_blank" rel="noopener noreferrer">
                      <Button className="bg-emerald-600 hover:bg-emerald-700">
                        Apply Now <ExternalLink className="ml-1 h-4 w-4" />
                      </Button>
                    </a>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-500" />
                        Requirements
                      </h4>
                      <ul className="space-y-2">
                        {form.requirements.map((req, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                            <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <HelpCircle className="h-4 w-4 text-purple-500" />
                        Tips for Success
                      </h4>
                      <ul className="space-y-2">
                        {form.tips.map((tip, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                            <span className="text-purple-500">💡</span>
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">For:</span>
                      <span className="text-gray-600">{form.forWhom}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* International Students Section */}
      <section className="py-12 bg-blue-50 border-t border-b border-blue-200">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-2xl">🌍</span>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold text-blue-800 mb-2">{internationalStudentInfo.title}</h3>
              <p className="text-blue-900/80 mb-4">{internationalStudentInfo.description}</p>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                {internationalStudentInfo.options.map((option, index) => (
                  <div key={index} className="bg-white/70 p-3 rounded-lg">
                    <div className="font-medium text-blue-800">{option.name}</div>
                    <div className="text-sm text-blue-700">{option.detail}</div>
                  </div>
                ))}
              </div>
              <Link to={internationalStudentInfo.link}>
                <Button className="bg-blue-600 hover:bg-blue-700">
                  International Student Resources <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Calculator className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Compare Your Aid Offers</h2>
          <p className="text-gray-600 mb-8">
            Once you receive financial aid award letters, use our tools to compare offers and make the best decision.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/compare">
              <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-8">
                Compare Colleges
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/paying-for-college">
              <Button size="lg" variant="outline" className="px-8">
                Scholarships & Loans
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FinancialAid;
